import { useMemo } from 'react';
import { formatDistanceToNow, format } from 'date-fns';
import {
  MessageSquare,
  Phone,
  Download,
  Eye,
  Share2,
  Activity,
  Mail,
  UserCheck,
  Undo2,
  ArrowRightLeft,
} from 'lucide-react';

const EMAIL_SENT = 'communication.email_sent';

const ICON_BY_TYPE = {
  'user.whatsapp_sent': MessageSquare,
  'user.message_sent': Phone,
  [EMAIL_SENT]: Mail,
  'client.document_downloaded': Download,
  'property.viewed': Eye,
  'property.shared': Share2,
  'property.claimed': UserCheck,
  'property.unclaimed': Undo2,
  'property.reassigned': ArrowRightLeft,
};

const LABEL_BY_TYPE = {
  'user.whatsapp_sent': 'WhatsApp sent',
  'user.message_sent': 'Message sent',
  'client.document_downloaded': 'Document downloaded',
  'property.viewed': 'Property viewed',
  'property.shared': 'Property shared',
  'property.claimed': 'Listing claimed',
  'property.unclaimed': 'Returned to import pool',
  'property.reassigned': 'Listing reassigned',
};

export const HistoryRow = ({ index, style, logs }) => {
  const log = logs[index];
  const Icon = ICON_BY_TYPE[log.event_type] || Activity;
  const isEmail = log.event_type === EMAIL_SENT;
  const recipient = log.payload?.name || log.payload?.email;
  const label = isEmail
    ? `Emailed to ${recipient || 'unknown'}`
    : LABEL_BY_TYPE[log.event_type] || log.event_type;

  const actorName = log.actor
    ? `${log.actor.first_name || ''} ${log.actor.last_name || ''}`.trim() ||
      'Unknown'
    : 'System';
  const details =
    isEmail && log.payload?.subject
      ? `${actorName} · ${log.payload.subject}`
      : actorName;
  const tooltip = isEmail
    ? [log.payload?.email, log.payload?.subject].filter(Boolean).join('\n')
    : undefined;

  const relativeTime = useMemo(
    () =>
      log.created_at
        ? formatDistanceToNow(new Date(log.created_at), { addSuffix: true })
        : '',
    [log.created_at]
  );

  const absoluteTime = useMemo(
    () => (log.created_at ? format(new Date(log.created_at), 'dd/MM/yy - HH:mm') : ''),
    [log.created_at]
  );

  return (
    <div
      style={style}
      title={tooltip}
      className="flex items-start gap-3 py-2 px-3 border-b border-gray-100 hover:bg-gray-50"
    >
      <div className="flex-shrink-0 mt-0.5 text-darkBlueColor">
        <Icon size={16} />
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium text-blackColor truncate">
            {label}
          </span>
          <span className="text-xs text-gray-500 flex-shrink-0">
            {relativeTime}
          </span>
        </div>

        <div className="flex items-baseline justify-between gap-2">
          <span className="text-xs text-gray-600 truncate">{details}</span>
          <span className="text-[11px] text-gray-400 flex-shrink-0 tabular-nums">
            {absoluteTime}
          </span>
        </div>
      </div>
    </div>
  );
};
